import { CacheStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { PRODUCTION_RUN, ProductionRunStatusValues, PRODUCTION_RUN_STATUS_LABELS, } from '../types/production-run.js';
import { USER_DATA } from '../types/user.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for ProductionRun (org/{orgId}/production-run).
 *
 * Top-level transactional collection. No lifecycle fields; the only lifecycle
 * is the business `status`. `stepId` is a plain pointer (subcollection).
 */
export const productionRunDescriptor = {
    name: PRODUCTION_RUN,
    label: 'Execução de produção',
    domain: 'production',
    collectionPath: 'org/{orgId}/production-run',
    pathPatternKeys: {
        orgId: 'string',
    },
    version: 1,
    description: 'Production run (execução) — interval of work against an OP (spec 0041 §2.2)',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            description: 'Unique production run identifier',
        },
        {
            name: 'productionOrderId',
            label: 'Ordem de produção',
            type: 'string',
            required: true,
            description: 'Owning production order (derived from the ticket when input is ticket-only)',
        },
        {
            name: 'ticketId',
            label: 'Talão',
            type: 'string',
            required: false,
            description: 'Optional production ticket; must belong to the OP when set',
        },
        {
            name: 'stepId',
            label: 'Etapa',
            type: 'string',
            required: false,
            description: 'Plain pointer to ProductionOrderStep (subcollection — no FK; resolved with OP context)',
        },
        {
            name: 'processId',
            label: 'Processo',
            type: 'string',
            required: false,
            description: 'Denormalized from the step when stepId is set; otherwise free',
        },
        {
            name: 'machineId',
            label: 'Máquina',
            type: 'string',
            required: false,
            description: 'Optional machine (not bound to the OP snapshot)',
        },
        {
            name: 'factoryId',
            label: 'Fábrica',
            type: 'string',
            required: false,
            description: 'Optional factory, filled by the cascade when omitted',
        },
        {
            name: 'operatorUserId',
            label: 'Operador',
            type: 'string',
            required: false,
            description: 'Who is running (may differ from inclusionUserId, who opened)',
        },
        {
            name: 'startedAt',
            label: 'Início',
            type: 'date',
            display: 'datetime',
            required: true,
            description: 'Start of the work interval (default now; backdating allowed)',
        },
        {
            name: 'finishedAt',
            label: 'Fim',
            type: 'date',
            display: 'datetime',
            required: false,
            description: 'End of the work interval (null while open)',
        },
        {
            name: 'durationMs',
            label: 'Duração (ms)',
            type: 'number',
            display: 'duration',
            required: false,
            description: 'Gross duration in ms (finishedAt − startedAt). Measured shop-floor time — not capped by MAX_DURATION_MS (that limit is for expected/planning times). Does not subtract stops.',
        },
        {
            name: 'status',
            label: 'Status',
            type: 'enum',
            required: true,
            enumValues: [...ProductionRunStatusValues],
            enumLabels: PRODUCTION_RUN_STATUS_LABELS,
            enumTones: {
                running: 'info',
                paused: 'warning',
                finished: 'success',
                cancelled: 'destructive',
            },
            description: 'Production run status (running → paused → running → finished | cancelled)',
            defaultValue: 'running',
        },
        {
            name: 'notes',
            label: 'Observações',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.NOTES,
        },
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'productionOrderId',
            targetEntity: 'ProductionOrder',
            relationship: 'many-to-one',
            displayField: 'code',
            description: 'Ordem de produção',
        },
        {
            fieldName: 'ticketId',
            targetEntity: 'ProductionTicket',
            relationship: 'many-to-one',
            displayField: 'ticketNumber',
            description: 'Talão',
        },
        {
            fieldName: 'processId',
            targetEntity: 'Process',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Processo',
        },
        {
            fieldName: 'machineId',
            targetEntity: 'Machine',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Máquina',
        },
        {
            fieldName: 'factoryId',
            targetEntity: 'Factory',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Fábrica',
        },
        {
            fieldName: 'operatorUserId',
            targetEntity: USER_DATA,
            relationship: 'many-to-one',
            displayField: 'name',
            expand: false,
            description: 'Operador (quem executa)',
        },
    ],
};
//# sourceMappingURL=production-run.descriptor.js.map